import { useMemo } from "react";

/**
 * Premium slider with a humanized value display.
 *
 * Supports three display formats:
 *   - "plain"           (default) — show the raw number with the optional unit.
 *   - "hm-from-minutes" — minutes integer (e.g. 110)   → "1h 50m"  · "110 min"
 *   - "hm-from-hours"   — hours decimal  (e.g. 7.25)   → "7h 15m"  · "435 min"
 *
 * Optional `ticks` (numeric values) render as discrete reference labels
 * beneath the bar, replacing the bare min/max captions. Each tick is
 * positioned by `(value - min) / (max - min)` and labelled with the same
 * humanized format used for the headline.
 */
export default function BigSlider({
  label,
  value,
  defaultValue,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  unit = "",
  display = "plain",
  ticks,
  testId,
}) {
  const isControlled = value != null && value !== "";
  const displayValue = isControlled ? Number(value) : defaultValue ?? (min + max) / 2;
  const pct = useMemo(() => {
    const range = max - min;
    if (!range) return 0;
    return Math.min(100, Math.max(0, ((displayValue - min) / range) * 100));
  }, [displayValue, min, max]);

  const formatted = useMemo(() => {
    return formatForDisplay(displayValue, { display, step, unit });
  }, [displayValue, step, display, unit]);

  const tickLabels = useMemo(() => {
    if (!ticks?.length) return null;
    return ticks.map((tickValue) => {
      const range = max - min;
      const left = range ? ((tickValue - min) / range) * 100 : 0;
      return {
        value: tickValue,
        left,
        label: tickShortLabel(tickValue, display, unit),
      };
    });
  }, [ticks, min, max, display, unit]);

  return (
    <div data-testid={testId}>
      <div className="flex items-end justify-between mb-2">
        <label className="tk-label">{label}</label>
      </div>

      <div className="flex items-baseline gap-3 mb-3">
        <span
          className={`font-outfit text-4xl sm:text-5xl tracking-tight transition-colors ${
            isControlled ? "text-sage-600" : "text-ink-600/50"
          }`}
          data-testid={`${testId}-value`}
        >
          {formatted.primary}
        </span>
        {formatted.secondary && (
          <span
            className="font-dmsans text-xs text-ink-600/80"
            data-testid={`${testId}-secondary`}
          >
            {formatted.secondary}
          </span>
        )}
      </div>

      <div className="relative">
        <div className="h-2 w-full rounded-full bg-sand-100 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-sage-400 to-sage-500 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={displayValue}
          // Auto-commit pattern (Sprint 7 V9): the first user interaction
          // — touch, mouse or keyboard — commits the displayed value.
          // Prevents the legacy bug where the slider thumb showed a
          // midpoint default that was never persisted to state.
          onPointerDown={() => { if (!isControlled) onChange(displayValue); }}
          onKeyDown={(e) => {
            if (!isControlled && /^(Arrow|Home|End|Page)/.test(e.key)) {
              onChange(displayValue);
            }
          }}
          onChange={(e) => onChange(Number(e.target.value))}
          className="absolute inset-0 w-full opacity-0 cursor-pointer"
          data-testid={`${testId}-input`}
        />
        <span
          className="pointer-events-none absolute top-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-sage-500 shadow-[0_3px_10px_rgba(42,157,143,0.4)] transition-transform"
          style={{ left: `calc(${pct}% - 10px)` }}
        />
      </div>

      {tickLabels ? (
        <div className="relative mt-3 h-4" aria-hidden="true">
          {tickLabels.map((t) => (
            <span
              key={t.value}
              className="absolute top-0 font-dmsans text-[10px] tracking-wide text-ink-600/70"
              style={{ left: `${t.left}%`, transform: "translateX(-50%)" }}
              data-testid={`${testId}-tick-${t.value}`}
            >
              {t.label}
            </span>
          ))}
          {/* Hairline ticks just under the bar for extra elegance */}
          {tickLabels.map((t) => (
            <span
              key={`mark-${t.value}`}
              className="absolute"
              style={{
                left: `${t.left}%`,
                transform: "translateX(-50%)",
                top: -10,
                width: 1,
                height: 4,
                background: "rgba(180, 165, 140, 0.5)",
              }}
            />
          ))}
        </div>
      ) : (
        <div className="flex justify-between mt-1.5 text-[11px] text-ink-600 font-dmsans">
          <span>{min}{unit && ` ${unit}`}</span>
          <span>{max}{unit && ` ${unit}`}</span>
        </div>
      )}
    </div>
  );
}

// ---------- helpers ----------

function formatHM(totalMinutes) {
  const t = Math.max(0, Math.round(totalMinutes));
  const h = Math.floor(t / 60);
  const m = t - h * 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

export function formatForDisplay(value, { display, step, unit }) {
  const n = Number(value);
  if (!Number.isFinite(n)) return { primary: "—", secondary: "" };

  if (display === "hm-from-minutes") {
    const total = Math.round(n);
    return { primary: formatHM(total), secondary: `${total} min` };
  }
  if (display === "hm-from-hours") {
    const totalMin = Math.round(n * 60);
    return { primary: formatHM(totalMin), secondary: `${totalMin} min` };
  }

  // plain — preserve previous behavior
  let primary;
  if (step < 1) {
    const decimals = Math.max(1, String(step).split(".")[1]?.length || 1);
    primary = n.toFixed(decimals);
  } else {
    primary = String(Math.round(n));
  }
  return { primary: unit ? `${primary} ${unit}` : primary, secondary: "" };
}

function tickShortLabel(tickValue, display, unit) {
  if (display === "hm-from-minutes") {
    // 0 → "0h", 60 → "1h", 240 → "4h"
    return `${Math.round(tickValue / 60)}h`;
  }
  if (display === "hm-from-hours") {
    return `${Math.round(tickValue)}h`;
  }
  return `${tickValue}${unit ? ` ${unit}` : ""}`;
}
